import { useIntl } from '@umijs/max';
import {
  Alert,
  Card,
  Descriptions,
  Modal,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { createStyles } from 'antd-style';
import dayjs from 'dayjs';
import React from 'react';
import TaskListPanel from '@/components/AdminTaskList/TaskListPanel';
import { IdcardOutlined } from '@/components/icon/tabler';
import LedgerPanel from '@/components/LedgerPanel';
import OrdersPanel from '@/components/OrdersPanel';
import UserAssetsPanel from '@/components/UserAssetsPanel';
import UserAvatar from '@/components/UserAvatar';
import { getAdminUserDetail } from '@/services/auth/api';
import { extractErrorMessage } from '@/services/auth/errorCodes';
import AccessPanel from './AccessPanel';
import CreditPanel from './CreditPanel';
import LoginHistoryPanel from './LoginHistoryPanel';

const { Text: AntText } = Typography;

const useStyles = createStyles(({ css }) => ({
  modalBody: css`
    height: min(80vh, 760px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 12px 24px 20px;
  `,
  spinWrap: css`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    /* Spin 嵌套 loading 时把内部 container 也撑成 flex column,让内容铺满 */
    .ant-spin-container {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
  `,
  topCard: css`
    flex: none;
    margin-bottom: 12px;
  `,
  tabsArea: css`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    /* antd Tabs 默认不会把 content-holder 撑满,这里强制 flex 布局 + active 面板内滚动 */
    .ant-tabs {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
    .ant-tabs-content-holder {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
    .ant-tabs-content {
      flex: 1;
      min-height: 0;
      height: 100%;
    }
    .ant-tabs-tabpane {
      height: 100%;
      overflow-y: auto;
    }
  `,
}));

export interface UserDetailModalProps {
  userId: number | null;
  open: boolean;
  onClose: () => void;

  includeTasksTab?: boolean;

  initialTab?: string;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  userId,
  open,
  onClose,
  includeTasksTab,
  initialTab,
}) => {
  const intl = useIntl();
  const { styles } = useStyles();

  const [loading, setLoading] = React.useState(false);
  const [user, setUser] = React.useState<AuthAPI.AdminUserDetail | null>(null);
  const [errorMsg, setErrorMsg] = React.useState<string>('');
  const [topupVersion, setTopupVersion] = React.useState(0);

  const loadDetail = React.useCallback(async (uid: number) => {
    setLoading(true);
    setErrorMsg('');
    try {
      const resp = await getAdminUserDetail(uid);
      if (resp?.success && resp?.data) {
        setUser(resp.data);
      } else {
        setErrorMsg(resp?.message || resp?.error?.code || 'load failed');
      }
    } catch (e: unknown) {
      setErrorMsg(extractErrorMessage(e, '加载用户详情失败'));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!open || !userId) return;
    setUser(null);
    void loadDetail(userId);
  }, [open, userId, loadDetail]);

  const oaProfileColumns: ColumnsType<AuthAPI.AdminOAProfile> = [
    {
      title: intl.formatMessage({
        id: 'pages.users.column.tenantName',
        defaultMessage: '租户',
      }),
      dataIndex: 'tenantName',
      width: 120,
    },
    {
      title: intl.formatMessage({
        id: 'pages.users.column.realName',
        defaultMessage: '真实姓名',
      }),
      dataIndex: 'realName',
      width: 100,
    },
    {
      title: intl.formatMessage({
        id: 'pages.users.column.phone',
        defaultMessage: '手机号',
      }),
      dataIndex: 'phone',
      width: 130,
      render: (text: string) => text || '-',
    },
    {
      title: intl.formatMessage({
        id: 'pages.users.column.deptName',
        defaultMessage: '部门',
      }),
      dataIndex: 'deptName',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: intl.formatMessage({
        id: 'pages.users.column.positionName',
        defaultMessage: '职位',
      }),
      dataIndex: 'positionName',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: intl.formatMessage({
        id: 'pages.users.column.status',
        defaultMessage: '状态',
      }),
      dataIndex: 'status',
      width: 80,
      render: (status: string) => <Tag>{status}</Tag>,
    },
  ];

  const items = [
    {
      key: 'info',
      label: intl.formatMessage({
        id: 'pages.users.detail.tab.info',
        defaultMessage: '基本信息',
      }),
      children: user && (
        <>
          <Descriptions
            column={2}
            bordered
            size="small"
            title={intl.formatMessage({
              id: 'pages.users.detail.basicInfo',
              defaultMessage: '基本信息',
            })}
          >
            <Descriptions.Item
              label={intl.formatMessage({
                id: 'pages.users.detail.email',
                defaultMessage: '邮箱',
              })}
            >
              {user.email ? (
                <Space size={4}>
                  <span>{user.email}</span>
                  {user.emailVerified ? (
                    <Tag>
                      {intl.formatMessage({
                        id: 'pages.users.detail.emailVerified',
                        defaultMessage: '已验证',
                      })}
                    </Tag>
                  ) : (
                    <Tag>
                      {intl.formatMessage({
                        id: 'pages.users.detail.emailNotVerified',
                        defaultMessage: '未验证',
                      })}
                    </Tag>
                  )}
                </Space>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={intl.formatMessage({
                id: 'pages.users.detail.phone',
                defaultMessage: '手机号',
              })}
            >
              {user.phone || '-'}
            </Descriptions.Item>
            <Descriptions.Item
              label={intl.formatMessage({
                id: 'pages.users.detail.loginMethods',
                defaultMessage: '登录方式',
              })}
            >
              {user.loginMethods && user.loginMethods.length > 0 ? (
                <Space size={4} wrap>
                  {user.loginMethods.map((m) => (
                    <Tag key={m}>{m}</Tag>
                  ))}
                </Space>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={intl.formatMessage({
                id: 'pages.users.detail.referralCode',
                defaultMessage: '推荐码',
              })}
            >
              {user.referralCode || '-'}
            </Descriptions.Item>
            <Descriptions.Item
              label={intl.formatMessage({
                id: 'pages.users.detail.lastLogin',
                defaultMessage: '最近操作',
              })}
            >
              {user.lastLogin
                ? dayjs(user.lastLogin).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item
              label={intl.formatMessage({
                id: 'pages.users.detail.createdAt',
                defaultMessage: '注册时间',
              })}
            >
              {user.createdAt
                ? dayjs(user.createdAt).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Descriptions.Item>
            {user.googleData && (
              <Descriptions.Item
                label={intl.formatMessage({
                  id: 'pages.users.detail.google',
                  defaultMessage: 'Google',
                })}
                span={2}
              >
                <Space>
                  <UserAvatar
                    src={user.googleData.picture}
                    name={user.googleData.name}
                    email={user.googleData.email}
                    size="small"
                  />
                  {user.googleData.name} ({user.googleData.email})
                </Space>
              </Descriptions.Item>
            )}
          </Descriptions>

          {user.oaProfiles && user.oaProfiles.length > 0 && (
            <>
              <div style={{ marginTop: 16, marginBottom: 12 }}>
                <Space>
                  <IdcardOutlined />
                  <AntText strong>
                    {intl.formatMessage({
                      id: 'pages.users.detail.oaProfiles',
                      defaultMessage: 'OA 档案',
                    })}
                  </AntText>
                  <Tag>
                    {intl.formatMessage(
                      {
                        id: 'pages.users.detail.tenantCount',
                        defaultMessage: '{count} 个租户',
                      },
                      { count: user.oaProfiles.length },
                    )}
                  </Tag>
                </Space>
              </div>
              <Table
                columns={oaProfileColumns}
                dataSource={user.oaProfiles}
                rowKey="tenantId"
                size="small"
                pagination={false}
                scroll={{ x: 800 }}
              />
            </>
          )}
        </>
      ),
    },
    ...(user
      ? [
          {
            key: 'loginHistory',
            label: intl.formatMessage({
              id: 'pages.users.detail.tab.loginHistory',
              defaultMessage: '登录历史',
            }),
            children: <LoginHistoryPanel userId={user.userId} />,
          },
        ]
      : []),
    ...(includeTasksTab && user?._id
      ? [
          {
            key: 'tasks',
            label: intl.formatMessage({
              id: 'pages.users.detail.tab.tasks',
              defaultMessage: '请求记录',
            }),

            children: (
              <TaskListPanel
                scopedUserId={user._id}
                showUserColumn={false}
                tableScrollY={420}
                nested
              />
            ),
          },
        ]
      : []),
    ...(user?._id
      ? [
          {
            key: 'credit',
            label: intl.formatMessage({
              id: 'pages.users.credit.tab',
              defaultMessage: '积分',
            }),
            children: (
              <CreditPanel
                userId={user.userId}
                onGranted={() => setTopupVersion((v) => v + 1)}
              />
            ),
          },
          {
            key: 'ledger',
            label: intl.formatMessage({
              id: 'ledger.title',
              defaultMessage: '消费记录',
            }),
            children: <LedgerPanel scopedUserId={user._id} scrollY={420} />,
          },
          {
            key: 'topup',
            label: intl.formatMessage({
              id: 'ledger.topup.title',
              defaultMessage: '充值记录',
            }),
            children: (
              <OrdersPanel
                scopedUserId={user._id}
                scrollY={420}
                refreshKey={topupVersion}
              />
            ),
          },
          {
            key: 'assets',
            label: intl.formatMessage({
              id: 'pages.users.detail.tab.assets',
              defaultMessage: '资产',
            }),
            children: <UserAssetsPanel ownerId={user._id} />,
          },
          {
            key: 'access',
            label: intl.formatMessage({
              id: 'pages.users.detail.tab.access',
              defaultMessage: '访问/设备',
            }),
            children: <AccessPanel userId={user._id} />,
          },
        ]
      : []),
  ];

  return (
    <Modal
      title={intl.formatMessage({
        id: 'pages.users.detail.title',
        defaultMessage: '用户详情',
      })}
      open={open}
      onCancel={onClose}
      footer={null}
      width={user ? 1080 : 720}
      centered
      classNames={{ body: styles.modalBody }}
      destroyOnHidden
    >
      <Spin spinning={loading} wrapperClassName={styles.spinWrap}>
        {errorMsg && (
          <Alert
            type="error"
            message={errorMsg}
            showIcon
            closable
            onClose={() => setErrorMsg('')}
            style={{ marginBottom: 12, flex: 'none' }}
          />
        )}
        {user && (
          <>
            {}
            <Card size="small" className={styles.topCard}>
              <Space size="middle" align="start" style={{ width: '100%' }}>
                <UserAvatar
                  size={56}
                  src={user.profile?.avatar}
                  name={user.profile?.nickname || user.email}
                  email={user.email}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Space size={8} wrap>
                    <AntText strong style={{ fontSize: 16 }}>
                      {user.profile?.nickname || user.email}
                    </AntText>
                    {user.email && (
                      <AntText type="secondary" style={{ fontSize: 12 }}>
                        {user.email}
                      </AntText>
                    )}
                  </Space>
                  <div style={{ marginTop: 6 }}>
                    <Space size={4} wrap>
                      <Tag>{user.status}</Tag>
                      {(user.roles || []).map((role) => (
                        <Tag key={role}>{role}</Tag>
                      ))}
                      <AntText type="secondary" style={{ fontSize: 12 }}>
                        uid: {user.userId}
                      </AntText>
                    </Space>
                  </div>
                </div>
              </Space>
            </Card>

            <div className={styles.tabsArea}>
              <Tabs
                defaultActiveKey={initialTab || 'info'}
                size="small"
                destroyOnHidden={false}
                items={items}
              />
            </div>
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default UserDetailModal;
